import { StatusBadge } from '@/components/ui/StatusBadge';
import { getRequestTypeConfig } from '@/data/requestsConfig';
import type { Request } from '@/pages/Admin/Requests';

const HEADERS = ['Referência', 'Tipo', 'Requerente', 'Data', 'Ficheiros', 'Estado', 'Tratado por'];

interface RequestsTableProps {
  requests: Request[];
  onRowClick: (request: Request) => void;
}

export function RequestsTable({ requests, onRowClick }: RequestsTableProps) {
  if (requests.length === 0) {
    return (
      <div className="text-on-surface-light flex flex-col items-center gap-2 py-12 text-center text-sm">
        <span className="material-symbols-outlined text-on-surface-light/40 text-3xl">inbox</span>
        Nenhum pedido encontrado.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[820px] border-collapse text-left">
        <thead>
          <tr className="border-line border-b">
            {HEADERS.map((h) => (
              <th
                key={h}
                className="text-on-surface-light/70 px-4 py-3 text-xs font-bold tracking-wider uppercase"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {requests.map((req) => {
            const config = getRequestTypeConfig(req.type);
            const name = (req.payload['name'] as string | undefined) ?? 'Anónimo';
            return (
              <tr
                key={req.id}
                onClick={() => onRowClick(req)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick(req);
                  }
                }}
                tabIndex={0}
                aria-label={`Abrir pedido de ${name}`}
                className="border-line hover:bg-background focus:bg-background cursor-pointer border-b transition-colors last:border-b-0 focus:outline-none"
              >
                <td className="text-primary px-4 py-3.5 text-xs font-bold tracking-wide">
                  {req.referenceCode}
                </td>
                <td className="text-on-surface px-4 py-3.5 text-sm font-semibold">
                  {config.label}
                </td>
                <td className="text-on-surface-light px-4 py-3.5 text-sm">{name}</td>
                <td className="text-on-surface-light px-4 py-3.5 text-xs">
                  {new Date(req.submittedAt).toLocaleDateString('pt-PT')}
                </td>
                <td className="px-4 py-3.5">
                  {req.files.length > 0 ? (
                    <span className="border-line bg-background text-on-surface-light inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs">
                      <span className="material-symbols-outlined text-[13px]">attach_file</span>
                      {req.files.length}
                    </span>
                  ) : (
                    <span className="text-on-surface-light/50 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3.5">
                  <StatusBadge status={req.status} />
                </td>
                <td className="text-on-surface-light px-4 py-3.5 text-xs">
                  {req.handledBy ?? '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
